<template>
  <div>
    <div class="hvh-flex column space-between grow-1" v-if="stackedChartYearlyData">
      <div>
        <div class="hvh-flex mb-2">
          <h2 class="hvh-h5">Yearly Distribution</h2>
          <button type="button" class="v-btn--icon theme--light" style="outline: none" @click="yearlyTooltip = true">
            <div class="v-btn__content">
              <v-icon class="primary--text" size="19">mdi-help-circle-outline</v-icon>
            </div>
          </button>
        </div>
        <div class="legend-box hvh-flex align-center mb-3">
          <div class="hvh-flex-center" v-for="option in yearlyLegend" :key="option.text">
            <svg class="legend-sqare" viewBox="0 0 12 12">
              <path :fill="option.color" d="M0 0h12v12H0V0z" />
            </svg>
            <label class="mx-2">{{ option.text }}</label>
          </div>
        </div>
      </div>
      <div>
        <StackedBarChart :options="barChartYearlyStackedOptions" :chart-data="stackedChartYearlyData" :scales="barChartYearlyStackedOptions" @data-created="yearlyStackedChartDataCreated" height="250px" @item-clicked="emitValue" />
      </div>
    </div>
    <div class="hvh-flex column space-between grow-1" v-else>
      <div>
        <div class="hvh-flex mb-2">
          <h2 class="hvh-h5">Yearly Distribution</h2>
          <button type="button" class="v-btn--icon theme--light" style="outline: none" @click="showYearlyTooltip">
            <div class="v-btn__content">
              <v-icon class="primary--text" size="19">mdi-help-circle-outline</v-icon>
            </div>
          </button>
        </div>
        <div class="legend-box hvh-flex align-center mb-3">
          <label style="font-weight: bold" v-if="canShowChildOption">
            <template v-for="(option, ind) in yearlyOptions">
              <span class="clickable" :class="{ underline: yearlyDistribution === option }" @click="yearlyDistribution = option" :key="option.text">{{ option.text }}</span>
              <template v-if="ind < yearlyOptions.length"> | </template>
            </template>
          </label>
        </div>
      </div>
      <div>
        <div class="no-data" v-if="!barChartYearlyData">No data available.</div>
        <HvhChart v-else :options="barChartYearlyOptions" :visualization="barChartYearlyData" height="250px" />
      </div>
    </div>
    <hvh-modal v-model="yearlyTooltip" title="What does Yearly Distribution show?" max-width="1100px" @input="yearlyTooltip = false">
      <template v-slot>
        <img :src="yearlyHelpImage" style="width: 100%" />
      </template>
    </hvh-modal>
  </div>
</template>

<script>
import { customNumberFormat } from '../../../../libraries/common';
import HvhChart from '../../../charts/HvhChart.vue';
import StackedBarChart from '../../../charts/StackedBarChart.vue';
import yearlyHelpImage from '../../../../assets/images/yearly-distribution.png';
export default {
  components: { StackedBarChart, HvhChart },
  props: ['cohort', 'value'],
  data() {
    const yearlyOptions = [
      { text: 'New', prop: 'new', },
      { text: 'Total', prop: 'total', },
      { text: 'Total Post-Index', prop: 'postIndex', }
    ];

    const yearlyStackedOptions = [
      { text: 'New Patients', prop: 'new', },
      { text: 'Post-Index', prop: 'postIndex', },
      { text: 'Pre-Index', prop: 'preIndex', }
    ];
    return {
      yearlyTooltip: false,
      yearlyLegend: [],
      yearlyOptions,
      yearlyStackedOptions,
      yearlyDistribution: yearlyOptions[1],
      barChartYearlyOptions: {
        grid: {
          left: '20px',
          right: '20px'
        },
        series: [
          {
            barMaxWidth: 30
          }
        ],
        tooltip: {
          formatter: params => {
            var count = !params.data.prop ? params.data.data[this.yearlyDistribution.prop] : params.data[params.data.prop];
            return `${params.name}<br>${params.marker}${customNumberFormat(count)}`;
          }
        },
        legend: {
          data: yearlyOptions.map(f => f.text),
        },
        yAxis: {
          axisLabel: {
            formatter: '{value}%'
          },
          name: 'Percent of Total',
          nameGap: 45
        }
      },
      barChartYearlyStackedOptions: {

        series: [
          {
            barMaxWidth: 30,
          }
        ],
        tooltip: {
          formatter: params => {
            var count = params.data[params.data.prop];
            return `${params.name}<br>${params.marker}${customNumberFormat(count)}`;
          }
        },
        legend: {
          data: yearlyStackedOptions.map(f => f.text),
        },
        yAxis: {
          axisLabel: {
            formatter: '{value}%'
          },
          name: 'Percent of Total',
          nameGap: 45
        }
      },
      yearlyHelpImage,
    };
  },
  computed: {
    barChartYearlyData() {
      var yearlyData = (this.cohort.cohortStatistic || {}).cohortYearCountStatistics;
      if (!yearlyData || yearlyData.length == 0)
        return undefined;
      return {
        data: yearlyData
          .map(f => ({
            ...f,
            count: Math.round(f[this.yearlyDistribution.prop] / this.cohort.cohortStatistic.totalCount * 10000) / 100,
          }))
          .sortBy(f => f.year),
        name_field: 'year',
        value_field: 'count',
        visualization_type: 'barchart',
      };
    },
    stackedChartYearlyData() {
      var currentYearlyData = this.cohort.cohortStatistic.cohortYearStackedCountStatistics;
      if (!(currentYearlyData || []).length) return undefined;

      var data = this.yearlyStackedOptions
        .map(opt => currentYearlyData
          .map((f, ind) => ({
            ...f,
            value: Math.round(f[opt.prop] / this.cohort.cohortStatistic.totalCount * 10000) / 100,
            label: f.year,
            name: opt.text,
            prop: opt.prop,
            color: !(this.value || []).any() || this.value.includes(f.year) ? undefined : 'rgb(216,216,216)',
            ind
          }))
          .sortBy(f => f.year)
        );

      return {
        datasets: [{ data }],
        scales: {},
        name_field: 'year',
        value_field: 'value',
      };
    }
  },
  methods: {
    showYearlyTooltip() {
      var tooltip = ['Total counts all active patients in the cohort per year.  New only counts patients in the year they meet the clinical criteria.  And Total Post-Index only counts patients the year they meet the clinical criteria and subsequent years where they remain active patients.'];
      this.alert('What does Yearly Distribution show?', tooltip.join('\n'));
    },
    yearlyStackedChartDataCreated(data) {
      this.yearlyLegend = this.barChartYearlyStackedOptions.legend.data
        .map((f, ind) => ({ text: f, color: data.color[ind] }));
    },
    emitValue(event) {
      var value = (this.value?.length !== this.stackedChartYearlyData.datasets[0].data[0].length && this.value) || [];
      var sortedValue = value
        .toggle(event.year)
        .sortBy(f => f);
      this.$emit('input', sortedValue);
    },
  },
}
</script>

<style lang="scss" scoped>
.legend-box {
  box-sizing: border-box;
  width: 100%;
  border: 1px solid #8ccac8;
  background-color: #f5fbfb;
  padding: 10px;

  label {
    line-height: 14px;
  }
  .legend-sqare {
    box-sizing: border-box;
    height: 12px;
    width: 12px;
    border: 0.25px solid #000000;
  }
}

.no-data {
  height: 250px;
  width: 100%;
  text-align: center;
  padding-top: 65px;
}
</style>